import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  api,
  type ApplyOutlineResponse,
  type OutlineApplySection,
  type OutlineSuggestionResponse,
  type ProjectCoverage,
  type ProposalSection,
  type SectionLinksResponse,
} from "@/lib/api";

export const sectionsKey = (projectId: string) => ["project", projectId, "sections"] as const;
export const sectionKey = (sectionId: string) => ["section", sectionId] as const;
export const sectionLinksKey = (sectionId: string) =>
  ["section", sectionId, "requirement-links"] as const;
export const coverageKey = (projectId: string) => ["project", projectId, "coverage"] as const;

export function useSections(projectId: string | null): UseQueryResult<ProposalSection[]> {
  return useQuery({
    queryKey: projectId ? sectionsKey(projectId) : ["project", null, "sections"],
    queryFn: () =>
      projectId ? api.listSections(projectId) : Promise.reject(new Error("no projectId")),
    enabled: Boolean(projectId),
  });
}

export function useSection(sectionId: string | null) {
  return useQuery({
    queryKey: sectionId ? sectionKey(sectionId) : ["section", null],
    queryFn: () =>
      sectionId ? api.getSection(sectionId) : Promise.reject(new Error("no sectionId")),
    enabled: Boolean(sectionId),
  });
}

export function useCreateSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createSection>[1]) =>
      api.createSection(projectId, body),
    onSuccess() {
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
    },
  });
}

export function useUpdateSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      patch,
    }: {
      sectionId: string;
      patch: Parameters<typeof api.updateSection>[1];
    }) => api.updateSection(sectionId, patch),
    onSuccess(_data, vars) {
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
      qc.invalidateQueries({ queryKey: sectionKey(vars.sectionId) });
    },
  });
}

export function useDeleteSection(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => api.deleteSection(sectionId),
    onSuccess() {
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
    },
  });
}

export function useGenerateSectionDraft(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string): Promise<ProposalSection> =>
      api.generateSectionDraft(sectionId),
    onSuccess(_data, sectionId) {
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
      qc.invalidateQueries({ queryKey: sectionKey(sectionId) });
    },
  });
}

export function useSuggestOutline(projectId: string) {
  return useMutation({
    mutationFn: (): Promise<OutlineSuggestionResponse> => api.suggestResponseOutline(projectId),
  });
}

export function useApplyOutline(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sections: OutlineApplySection[]): Promise<ApplyOutlineResponse> =>
      api.applyResponseOutline(projectId, sections),
    onSuccess() {
      qc.invalidateQueries({ queryKey: sectionsKey(projectId) });
    },
  });
}

export function useSectionRequirementLinks(
  sectionId: string | null,
): UseQueryResult<SectionLinksResponse> {
  return useQuery({
    queryKey: sectionId ? sectionLinksKey(sectionId) : ["section", null, "requirement-links"],
    queryFn: (): Promise<SectionLinksResponse> =>
      sectionId
        ? api.listSectionRequirementLinks(sectionId)
        : Promise.reject(new Error("no sectionId")),
    enabled: Boolean(sectionId),
  });
}

export function useReplaceSectionRequirementLinks(projectId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matrixItemIds: string[]): Promise<SectionLinksResponse> =>
      api.replaceSectionRequirementLinks(sectionId, matrixItemIds),
    onSuccess() {
      qc.invalidateQueries({ queryKey: sectionLinksKey(sectionId) });
      qc.invalidateQueries({ queryKey: coverageKey(projectId) });
    },
  });
}

export function useProjectCoverage(projectId: string | null): UseQueryResult<ProjectCoverage> {
  return useQuery({
    queryKey: projectId ? coverageKey(projectId) : ["project", null, "coverage"],
    queryFn: (): Promise<ProjectCoverage> =>
      projectId ? api.getProjectCoverage(projectId) : Promise.reject(new Error("no projectId")),
    enabled: Boolean(projectId),
  });
}
